import React, { useState, useRef, useEffect } from 'react';
import './Projects.css';
import { motion } from "framer-motion";
import amplitude from '../amplitude';


function Projects() {
  // State to manage the flipped status of cards
  const [flipped, setFlipped] = useState([false, false, false, false]);


  // State to manage button states
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Reference to the scrollable container
  const scrollContainerRef = useRef(null);

  // Function to handle card flip
  const handleFlip = (index) => {
    const newFlipped = [...flipped];
    newFlipped[index] = !newFlipped[index];
    setFlipped(newFlipped);

    const projectNames = ['Syncable', 'PlatePal', 'SafeScript'];
    amplitude.track('Project Card Flipped', {
      project: projectNames[index],
      flipped: newFlipped[index] ? 'Front to Back' : 'Back to Front',
    });
    
  };

  // Function to scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  // Function to scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Check scroll position and update button states
  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft < maxScrollLeft);
    }
  };

  // Attach scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
    }
    checkScrollPosition(); // Initial check on mount

    // Cleanup event listener on unmount
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, []);

  return (
    <div id="projects" className="projects-section">
      <motion.div
        className="section-title projects-title"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <h1>Projects. <span>Bringing my ideas to life.</span></h1>
        </motion.div>
      <div className="scrollable-wrapper" ref={scrollContainerRef}>
        <div className="projects-container">

          {/* First Card */}
          <div className="project-card">
            <div className={`card-inner ${flipped[0] ? 'flipped' : ''}`}>
              {/* Card Front */}
              <div className="card-front">
                <div className="card-content">
                  <p className="project-title1">Syncable</p>
                  <p className="project-type1">Web App</p>
                  <div className="icon more" onClick={() => handleFlip(0)}>
                    <img src="/images/morecard_w.png" alt="Flip Icon" className="flip-icon flip-icon1" />
                  </div>
                  <img src="/images/syncablelogo.png" alt="Syncable" className="project-image1" />
                </div>
              </div>
              {/* Card Back */}
              <div className="card-back">
                <h3 className="slogan-text1">Sync your timetable, seamlessly.</h3>
                <h3 className="project-titleback1">Syncable</h3>
                <h3 className="project-position1">Founder</h3>
                <img src="/images/ht.png" alt="htmllogo" className="project-lang1" />
                {/* Description */}
                <p className="project-description1">
                  <span className="bold-text">Syncable is an intelligent scheduling solution built for students.</span> Leveraging advanced data parsing and recognition techniques to streamline timetable management. Syncable uses <span className="bold-text">OCR</span> and data extraction to parse uploaded timetables and automatically sync them with your <span className="bold-text">Google Calendar.</span>
                </p>
                {/* Buttons */}
                <div className="button-container">
                  <a href="https://syncableapp.netlify.app/" target="_blank" rel="noopener noreferrer" className="link-button">
                    Visit Syncable <span className="arrow">↗</span>
                  </a>
                </div>
                {/* GitHub Button */}
                <div className={`github-button-container`}>
                  <div className="github-logo-wrapper">
                    <a href="https://github.com/aboudjalal/Syncable" target="_blank" rel="noopener noreferrer">
                      <img src="/images/github_logo_w.png" alt="GitHub Logo" className="github-logo" />
                    </a>
                  </div>
                </div>
                <div className="icon close" onClick={() => handleFlip(0)}>
                  <img src="/images/closecard_w.png" alt="Flip Icon" className="flip-icon" />
                </div>
              </div>
            </div>
          </div>

          {/* Second Card */}
          <div className="project-card">
            <div className={`card-inner ${flipped[1] ? 'flipped' : ''}`}>
              {/* Card Front */}
              <div className="card-front">
                <div className="card-content">
                  <p className="project-title2">PlatePal</p>
                  <p className="project-type2">iOS App</p>
                  <div className="icon more" onClick={() => handleFlip(1)}>
                    <img src="/images/morecard.png" alt="Flip Icon" className="flip-icon flip-icon2" />
                  </div>
                  <img src="/images/platepallogo.png" alt="plaepal" className="project-image2" />
                </div>
              </div>
              {/* Card Back */}
              <div className="card-back">
                <h3 className="slogan-text2">Discover your next favorite food.</h3>
                <h3 className="project-titleback2">PlatePal</h3>
                <h3 className="project-position2">Co-Founder</h3>
                <img src="/images/swift-logo.png" alt="swiftlogo" className="project-lang2" />
                {/* Description */}
                <p className="project-description2">
                  PlatePal is a fun and interactive meal discovery app that lets users swipe through a variety of meals, saving the ones they like. With each saved meal, users can access detailed recipes and step-by-step cooking instructions, making it easy to explore new dishes and enhance their cooking experience.
                </p>
                {/* Buttons */}
                <div className="button-container">
                <a href="https://apps.apple.com/ca/app/platepal/id6741442946" target="_blank" rel="noopener noreferrer">
                    <img src="/images/iosdownload.png" alt="Download PlatePal" className="download-button" /> 
                    {/* change this "download-button" for download button style */}
                  </a>
                </div>
                {/* GitHub Button */}
                <div className={`github-button-container`}>
                  <div className="github-logo-wrapper">
                    {/* <a href="https://github.com/aboudjalal/Syncable" target="_blank" rel="noopener noreferrer">
                      <img src="/images/github_logo.png" alt="GitHub Logo" className="github-logo" />
                    </a> */}
                  </div>
                </div>
                <div className="icon close" onClick={() => handleFlip(1)}>
                  <img src="/images/closecard.png" alt="Flip Icon" className="flip-icon" />
                </div>
              </div>
            </div>
          </div>



          {/* Third Card */}
          <div className="project-card">
            <div className={`card-inner ${flipped[2] ? 'flipped' : ''}`}>
              {/* Card Front */}
              <div className="card-front">
                <div className="card-content">
                  <p className="project-title3">SafeScript</p>
                  <p className="project-type3">Visual Studio Code Extension </p>
                  <div className="icon more" onClick={() => handleFlip(2)}>
                    <img src="/images/morecard.png" alt="Flip Icon" className="flip-icon flip-icon3" />
                  </div>
                  <img src="/images/SafeScript.png" alt="Safescript" className="project-image3" />
                </div>
              </div>
              {/* Card Back */}
              <div className="card-back">
                <h3 className="slogan-text3">Secure your code.</h3>
                <h3 className="project-titleback3">SafeScript</h3>
                <h3 className="project-position3">Co-Founder</h3>
                <img src="/images/ts_logo.png" alt="tslogo"className="project-lang3" />
                {/* Description */}
                <p className="project-description3">
                SafeScript is a VS Code extension that integrates Code Llama to provide AI-powered code analysis and security vulnerability detection. Designed for developers leveraging LLM-generated code, SafeScript helps identify potential security flaws, ensuring safer and more reliable software development.                </p>
                {/* Buttons */}
                <div className="button-container">
                <a href="https://marketplace.visualstudio.com/items?itemName=SafeScript.SafeScript" target="_blank" rel="noopener noreferrer">
                    <img src="/images/vscode.png" alt="Download Safescript" className="download-button3" />
                    <span className="install-text">Extension</span>
                    {/* change this "download-button" for download button style */}
                  </a>
                </div>
                {/* GitHub Button */}
                <div className={`github-button-container`}>
                  <div className="github-logo-wrapper">
                    {/* <a href="https://github.com/aboudjalal/Syncable" target="_blank" rel="noopener noreferrer">
                      <img src="/images/github_logo.png" alt="GitHub Logo" className="github-logo" />
                    </a> */}
                  </div>
                </div>
                <div className="icon close" onClick={() => handleFlip(2)}>
                  <img src="/images/closecard.png" alt="Flip Icon" className="flip-icon" />
                </div>
              </div>
            </div>
          </div>






        </div>
      </div>
      <div className="scroll-buttons">
        <button
          className={`scroll-button left ${!canScrollLeft ? 'leftless' : 'leftmore'}`}
          onClick={scrollLeft}
          disabled={!canScrollLeft}
        >
          <img src={canScrollLeft ? "/images/leftmore.png" : "/images/leftless.png"} alt="Scroll Left" className="arrow-icon" />
        </button>
        <button
          className={`scroll-button right ${!canScrollRight ? 'rightless' : 'rightmore'}`}
          onClick={scrollRight}
          disabled={!canScrollRight}
        >
          <img src={canScrollRight ? "/images/rightmore.png" : "/images/rightless.png"} alt="Scroll Right" className="arrow-icon" />
        </button>
      </div>
    </div>
  );
}

export default Projects;
